import React from 'react';
import PropTypes from 'prop-types';
import { Form, Input, Select } from 'semantic-ui-react';
import InputNumberFormat from 'components/common/InputNumberFormat';
import { toDropDownOptions } from 'utils/dropdownOptions';
import MyDropzone from 'components/common/DragAndDrop';
import UserSearch from 'components/common/UserSearch';
import GroupSearch from 'components/common/GroupSearch';
import saveWalletTypes from 'constants/options/saveWalletTypes';
import formatNumber from 'helpers/formatNumber';
import {
  CREDIT_USER_TEMPLATE,
  CREDIT_GROUP_TEMPLATE,
} from 'constants/Templates';

const beneficiaryOptions = toDropDownOptions(['group', 'user']);

const CreditOrganizationForm = ({
  form,
  onChange,
  onFileChange,
  file,
  currentOrgId,
  walletItems,
}) => {
  const handleChange = (e, { name, value }) => {
    onChange(name, value);
  };

  const label = form.filter === 'group' ? 'Group' : 'User';

  const saveWallet = walletItems?.find(w =>
    w?.provider_name?.toLowerCase()?.includes('save'),
  );

  const selectedBalance = form.source
    ? saveWallet?.[form.source]
    : null;

  return (
    <div>
      <Form.Field
        fluid
        required
        name="filter"
        control={Select}
        options={beneficiaryOptions}
        label="Beneficiary Type"
        placeholder="Select"
        onChange={handleChange}
      />

      {form.filter && (
        <Form.Group
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Form.Radio
            checked={!form.bulk}
            label={`Single ${label}`}
            onChange={() => onChange('bulk', false)}
          />
          <Form.Radio
            checked={!!form.bulk}
            label={`Multiple ${label}s`}
            onChange={() => onChange('bulk', true)}
          />
        </Form.Group>
      )}

      {form.filter === 'group' && !form.bulk && (
        <>
          <GroupSearch
            required
            name="group"
            label="Group"
            organizationId={currentOrgId}
            form={form}
            handleChange={handleChange}
            error={false}
            showIcon
          />
          {form.group && (
            <div className="credit-org-form__balance">
              <span className="credit-org-form__balance--label">
                ID
              </span>
              <span className="credit-org-form__balance--value">
                {form.group}
              </span>
            </div>
          )}
        </>
      )}

      {form.filter === 'user' && !form.bulk && (
        <>
          <UserSearch
            name="user"
            handleChange={(e, { name, value, user }) => {
              onChange(name, user ? user.id : value);
            }}
            error={false}
            allowAdditions={false}
            showIcon
          />
          {form.user && (
            <div className="credit-org-form__balance">
              <span className="credit-org-form__balance--label">
                ID
              </span>
              <span className="credit-org-form__balance--value">
                {form.user}
              </span>
            </div>
          )}
        </>
      )}

      {form.filter && form.bulk && (
        <MyDropzone
          label={`Attach ${label} list`}
          setFile={onFileChange}
          file={file}
          accept=".xlsx, .xls, .csv"
          showPreview={false}
          variant="spreadsheet"
          templateUrl={
            form.filter === 'group'
              ? CREDIT_GROUP_TEMPLATE
              : CREDIT_USER_TEMPLATE
          }
        />
      )}

      {form.filter && (
        <>
          <Form.Field
            required
            name="source"
            control={Select}
            fluid
            label="Wallet"
            placeholder="Select"
            onChange={handleChange}
            options={toDropDownOptions(saveWalletTypes, true)}
          />
          {form.source && (
            <div className="credit-org-form__balance">
              <span className="credit-org-form__balance--label">
                Available balance
              </span>
              <span className="credit-org-form__balance--value">
                {formatNumber(selectedBalance ?? 0)} Rwf
              </span>
            </div>
          )}
        </>
      )}

      {!form.bulk && (
        <InputNumberFormat
          required
          name="amount"
          control={Input}
          label="Amount"
          type="number"
          placeholder="Amount..."
          onChange={handleChange}
        />
      )}

      <Form.TextArea
        required
        label="Description"
        placeholder="Type something..."
        name="description"
        className="textarea"
        onChange={handleChange}
      />
    </div>
  );
};

CreditOrganizationForm.propTypes = {
  form: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onFileChange: PropTypes.func.isRequired,
  file: PropTypes.instanceOf(Object),
  currentOrgId: PropTypes.number,
  walletItems: PropTypes.instanceOf(Array),
};

CreditOrganizationForm.defaultProps = {
  file: {},
  currentOrgId: null,
  walletItems: [],
};

export default CreditOrganizationForm;
